import { Component } from "@theme/component";
import { VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph, MORPH_OPTIONS } from "@theme/morph";
import { yieldToMainThread, getViewParameterValue, ResizeNotifier, startViewTransition } from "@theme/utilities";

const CHECKED_TRUE = "true";
const CHECKED_FALSE = "false";
const FEATURED_PRODUCT_INFORMATION = "featured-product-information";
const MAIN_ELEMENT_SELECTOR = "main";

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export class VariantPicker extends Component {
  /** @type {string | null | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach(fieldset => {
      const radios = [...(fieldset?.querySelectorAll("input") ?? [])];
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex(radio => radio.dataset.currentChecked === CHECKED_TRUE);
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener("change", this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) {
      return;
    }

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) {
      return;
    }

    this.updateSelectedOption(event.target);
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? "",
      }),
    );

    const isOnProductPage = this.dataset.templateProductMatch === CHECKED_TRUE;

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split("?")[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && Boolean(newUrl) && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest(FEATURED_PRODUCT_INFORMATION));

    let morphElementSelector;
    if (loadsNewProduct) {
      morphElementSelector = MAIN_ELEMENT_SELECTOR;
    } else if (isOnFeaturedProductSection) {
      morphElementSelector = FEATURED_PRODUCT_INFORMATION;
    } else {
      // morphElementSelector remains undefined; only the variant picker will be morphed
    }

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(globalThis.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set("variant", variantId);
      } else {
        url.searchParams.delete("variant");
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl || "";
    }

    if (url.href !== globalThis.location.href) {
      yieldToMainThread()
        .then(() => {
          history.replaceState({}, "", url.toString());
        })
        .catch(() => {
          // ignore error
        });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) {
      return null;
    }

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex === undefined ? undefined : radios[currentIndex]?.parentElement?.offsetWidth,
      previousWidth: previousIndex === undefined ? undefined : radios[previousIndex]?.parentElement?.offsetWidth,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty("--pill-width-current", `${currentWidth}px`);
    } else if (currentIndex === undefined) {
      // neither currentWidth nor currentIndex present; nothing to apply
    } else {
      fieldset.style.removeProperty("--pill-width-current");
    }

    if (previousWidth) {
      fieldset.style.setProperty("--pill-width-previous", `${previousWidth}px`);
    } else if (previousIndex === undefined) {
      // neither previousWidth nor previousIndex present; nothing to apply
    } else {
      fieldset.style.removeProperty("--pill-width-previous");
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) {
      return;
    }

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    let resolvedTarget = target;
    if (typeof resolvedTarget === "string") {
      const targetElement = this.querySelector(`[data-option-value-id="${resolvedTarget}"]`);

      if (!targetElement) {
        throw new Error("Target element not found");
      }

      resolvedTarget = targetElement;
    }

    if (resolvedTarget instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(resolvedTarget.dataset.fieldsetIndex || "", 10);
      const inputIndex = Number.parseInt(resolvedTarget.dataset.inputIndex || "", 10);

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = CHECKED_FALSE;
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = CHECKED_FALSE;
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states (newCurrentIndex is always inputIndex; newPreviousIndex might be undefined)
          const [newCurrentIndex, newPreviousIndex] = checkedIndices;

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = CHECKED_TRUE;
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = CHECKED_TRUE;
            radios[newPreviousIndex].dataset.currentChecked = CHECKED_FALSE;
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      resolvedTarget.checked = true;
    }

    if (resolvedTarget instanceof HTMLSelectElement) {
      const newValue = resolvedTarget.value;
      const newSelectedOption = [...resolvedTarget.options].find(option => option.value === newValue);

      if (!newSelectedOption) {
        throw new Error("Option not found");
      }

      for (const option of resolvedTarget.options) {
        option.removeAttribute("selected");
      }

      newSelectedOption.setAttribute("selected", "selected");
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) {
      params.push(`view=${viewParamValue}`);
    }

    if (this.selectedOptionsValues.length > 0) {
      params.push(`option_values=${this.selectedOptionsValues.join(",")}`);
    }

    // Featured products render through a section-specific response.
    const sectionIdMap = {
      [FEATURED_PRODUCT_INFORMATION]: this.closest(FEATURED_PRODUCT_INFORMATION)?.id,
    };

    const closestSectionId = /** @type {keyof typeof sectionIdMap} | undefined */ (
      Object.keys(sectionIdMap).find(sectionId => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return `${productUrl}?section_id=${sectionIdMap[closestSectionId]}&${params.join("&")}`;
    }

    return `${productUrl}?${params.join("&")}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then(response => response.text())
      .then(async responseText => {
        this.#pendingRequestUrl = null;
        const html = new DOMParser().parseFromString(responseText, "text/html");

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) {
          return;
        }

        let newProduct;

        // Resolve the gallery-cross-fade lookup scope to the morph target. Pages with
        // multiple `<media-gallery>` instances (e.g., a featured-product-information
        // section alongside a separate PDP gallery) need this hint so the registry
        // names the gallery inside the morph target rather than the first match in
        // document order. Mirror `updateMain` / `updateElement` resolution: `<main>` is
        // a singleton (`document.querySelector`); a custom-element ancestor selector
        // resolves to the closest enclosing instance (`this.closest`). When
        // `morphElementSelector` is undefined only the variant-picker subtree morphs —
        // the gallery (a sibling of the picker) is not replaced, so passing `this`
        // makes the registry no-op cleanly.
        const scopeElement = (pickerElement => {
          if (morphElementSelector === MAIN_ELEMENT_SELECTOR) {
            return document.querySelector(MAIN_ELEMENT_SELECTOR) ?? document;
          }
          if (morphElementSelector) {
            return pickerElement.closest(morphElementSelector) ?? document;
          }
          return pickerElement;
        })(this);

        const updateDom = () => {
          if (morphElementSelector === MAIN_ELEMENT_SELECTOR) {
            this.updateMain(html);
          } else if (morphElementSelector) {
            this.updateElement(html, morphElementSelector);
          } else {
            newProduct = this.updateVariantPicker(html);
          }
        };

        // The `gallery-cross-fade` transition only has anything to animate when a
        // `<media-gallery>` lives inside the morph target: the combined-listing
        // `updateMain` path (scope `<main>`) and the featured-product path (scope
        // `featured-product-information`). For a plain PDP variant-picker-only morph,
        // the scope is the picker itself, which never contains a gallery, so the type
        // setup names no element and there is nothing to cross-fade.
        if (scopeElement.querySelector("media-gallery")) {
          await startViewTransition(updateDom, ["gallery-cross-fade"], { scopeElement });
        } else {
          updateDom();
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              newProduct,
              productId: this.dataset.productId ?? "",
            }),
          );
        }
      })
      .catch(error => {
        if (error.name === "AbortError") {
          // Log the error or warning
          console.warn("Fetch aborted by user");
        } else {
          // Log the error or warning
          console.error(error);
          // Unblock product-form's #variantChangeInProgress so the user can retry.
          // AbortError is excluded: the next swatch click already sets its own in-progress state.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(/** @type {any} */ (null), this.selectedOptionId, {
                html: new DOMParser().parseFromString("", "text/html"),
                productId: this.dataset.productId ?? "",
              }),
            );
          }
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found");
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: node => (node instanceof HTMLElement ? (node.dataset.key ?? undefined) : undefined),
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets
      .map((_, index) => this.#getFieldsetMeasurements(index))
      .filter(measurement => measurement !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector(MAIN_ELEMENT_SELECTOR);
    const newMain = newHtml.querySelector(MAIN_ELEMENT_SELECTOR);

    if (!main || !newMain) {
      throw new Error("No new main source found");
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector("select option[selected], fieldset input:checked");

    return selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement
      ? selectedOption
      : undefined;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (selectedOption && !selectedOption.dataset.optionValueId) {
      throw new Error("No option value ID found");
    }
    return selectedOption?.dataset.optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type {HTMLElement[]} */
    const selectedOptions = /** @type {any} */ ([
      ...this.querySelectorAll("select option[selected], fieldset input:checked"),
    ]);

    return selectedOptions.map(option => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) {
        throw new Error("No option value ID found");
      }

      return optionValueId;
    });
  }
}

if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}
